import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Tooltip, Typography } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import { regionName, regionCurrency } from './regions';

export const PERIODS = ['monthly', 'quarterly', 'yearly'];
const PERIOD_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

/**
 * The stand-in id written for a period that is priced but not sellable. The
 * `rzp_plan_` prefix is load-bearing: the upgrade button checks for it and
 * sends people to support rather than a checkout that cannot complete.
 */
export const placeholderPlanId = (planKey, region, period) =>
    `rzp_plan_${planKey}_${String(region).toLowerCase()}_${period}`;

export const isPlaceholderConfig = (config) =>
    Boolean(config?.placeholder) || String(config?.plan_id || '').startsWith('rzp_plan_');

/**
 * A price is only sellable with a real Razorpay plan behind it.
 */
const cellState = (price, config) => {
    if (!price) return { status: 'unsold' };
    if (!config?.plan_id) return { status: 'broken', note: 'Priced, but no Razorpay plan id. Checkout fails here.' };
    if (isPlaceholderConfig(config)) {
        return { status: 'placeholder', note: 'Marked as a placeholder. Priced on the page, but nobody can buy it.' };
    }
    return { status: 'sold', note: config.plan_id };
};

/**
 * What this plan costs, by region and billing period.
 *
 * A region's prices used to read as one run-on line per region, which hid the
 * two things worth knowing: which periods are actually sold, and whether a
 * price has a working Razorpay plan behind it. In a grid, a gap is visible as
 * a gap, and the region a price belongs to is its row rather than a prefix.
 */
const PriceMatrix = ({ plan }) => {
    const theme = useTheme();

    const regions = Object.keys(plan?.pricing || plan?.razorpay_config || {});
    const priced = regions.some((region) =>
        PERIODS.some((period) => plan?.pricing?.[region]?.[period]));

    if (!regions.length || !priced) {
        return (
            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.customColors.g200 }}>
                Free in every region. No Razorpay plans needed.
            </Typography>
        );
    }

    const columnTemplate = `minmax(8rem, 0.8fr) repeat(${PERIODS.length}, 1fr)`;

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: '34rem' }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: columnTemplate,
                    gap: '0.75rem',
                    padding: '0 0.75rem 0.5rem',
                }}>
                    <span />
                    {PERIODS.map((period) => (
                        <Typography key={period} sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: theme.palette.customColors.g200,
                        }}>
                            {PERIOD_LABELS[period]}
                        </Typography>
                    ))}
                </Box>

                <Box display="grid" gap="0.375rem">
                    {regions.map((region) => {
                        const pricing = plan.pricing?.[region] || {};

                        return (
                            <Box key={region} sx={{
                                display: 'grid',
                                gridTemplateColumns: columnTemplate,
                                gap: '0.75rem',
                                alignItems: 'center',
                                padding: '0.625rem 0.75rem',
                                borderRadius: '8px',
                                backgroundColor: theme.palette.customColors.yellow_bg,
                                border: `1px solid ${theme.palette.customColors.b50}`,
                            }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                        {regionName(region)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: theme.palette.customColors.g100 }}>
                                        {regionCurrency(pricing, region)}
                                    </Typography>
                                </Box>

                                {PERIODS.map((period) => {
                                    const price = pricing[period];
                                    const config = plan.razorpay_config?.[region]?.[period];
                                    const { status, note } = cellState(price, config);

                                    if (status === 'unsold') {
                                        return (
                                            <Typography key={period} sx={{
                                                fontSize: '0.875rem',
                                                color: theme.palette.customColors.g75,
                                            }}>
                                                —
                                            </Typography>
                                        );
                                    }

                                    const needsAttention = status === 'broken' || status === 'placeholder';

                                    return (
                                        <Tooltip key={period} title={note || ''} placement="top">
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Typography sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: needsAttention
                                                        ? theme.palette.customColors.g100
                                                        : theme.palette.customColors.g300,
                                                }}>
                                                    {`${pricing.symbol || ''}${Number(price).toLocaleString('en-IN')}`}
                                                </Typography>
                                                {needsAttention && (
                                                    <WarningAmberRounded sx={{
                                                        fontSize: '0.9375rem',
                                                        color: theme.palette.customColors.r400,
                                                    }} />
                                                )}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default PriceMatrix;
